const GENERIC_MESSAGES = ["Invalid value.", "Invalid value"];

const NETWORK_ERROR_PATTERNS = [
  "Load failed",
  "Failed to fetch",
  "NetworkError",
  "network error",
  "Network request failed",
  "net::ERR_",
];

const OVERLOADED_PATTERNS = ["Overloaded", "overloaded"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFn = (key: any) => string | undefined;

let globalTranslate: TranslateFn | undefined;

export function setErrorTranslator(fn: TranslateFn) {
  globalTranslate = fn;
}

function isGenericMessage(msg: string): boolean {
  return GENERIC_MESSAGES.includes(msg);
}

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "";
}

export function isNetworkError(err: unknown): boolean {
  if (!err) return false;
  const message = getErrorMessage(err);
  return NETWORK_ERROR_PATTERNS.some((pattern) =>
    message.toLowerCase().includes(pattern.toLowerCase()),
  );
}

export function isOverloadedError(err: unknown): boolean {
  if (!err) return false;
  const message = getErrorMessage(err);
  return OVERLOADED_PATTERNS.some((pattern) => message.includes(pattern));
}

/** Finds an error message in a nested object. Prefers deeper nested message unless generic. */
export function findErrorMessage(
  err: unknown,
  translate?: TranslateFn,
): string | undefined {
  const t = translate ?? globalTranslate;

  if (!err) {
    return t?.("error.unknown") ?? "Unknown Error";
  }

  if (isNetworkError(err)) {
    return (
      t?.("error.network") ??
      "Connection lost. Please check your internet connection and try again."
    );
  }

  if (isOverloadedError(err)) {
    return (
      t?.("error.overloaded") ??
      "The service is temporarily overloaded. Please try again in a moment."
    );
  }

  if (typeof err === "string") {
    return err;
  }

  if (typeof err !== "object") {
    return t?.("error.unknown") ?? "Unknown Error";
  }

  const errObj = err as Record<string, unknown>;

  if (errObj.status === 404) {
    return t?.("error.notFound") ?? "Not found";
  }

  const nestedProps = Object.values(errObj).filter(
    (v) => !!v && typeof v === "object",
  );

  if (nestedProps.length > 0) {
    const nestedMessages = nestedProps.map((p) => findErrorMessage(p));
    const meaningfulMessage = nestedMessages.find(
      (msg) => msg && !isGenericMessage(msg),
    );
    if (meaningfulMessage) {
      return meaningfulMessage;
    }
  }

  return (errObj.body || errObj.message || errObj.error || errObj.msg) as
    | string
    | undefined;
}
